import { useQuery } from '@tanstack/react-query'
import {
  v2GetPortfolioOutreachStatusOptions,
  v2GetPortfolioQueueOptions,
} from '@workspace/api-backend/react-query'
import type { NavBadgeValue } from '@workspace/seedforge-ui/components/app-sidebar'
import { useRef } from 'react'
import { useStartupFeatureFlagsQuery } from '../../feature-flags/hooks/use-startup-feature-flags'
import { type HomeStateData, useHomeStateQuery } from '../../home/hooks/use-home-state'
import { resolveOutreachNavBadges } from '../lib/nav-badges'
import { useOutreachQueueQuery } from './use-outreach-queue'
import { useOutreachReplies } from './use-outreach-replies'
import { useOutreachStatusQuery } from './use-outreach-status'

/**
 * The states before a campaign has ever run. Home is walking the founder
 * through these one card at a time, and the campaign boards hold nothing they
 * can act on yet — so the sidebar stays quiet until there is a live campaign
 * for a dot to be about.
 */
const SETUP_STATES: ReadonlySet<HomeStateData['key']> = new Set([
  'QUEST_REVIEW_MESSAGES',
  'QUEST_CONNECT_LINKEDIN',
  'QUEST_PREPARING_LINK',
  'QUEST_LAUNCH_CAMPAIGN',
])

/**
 * Nav badges for the Outreach section. The rules live in `resolveOutreachNavBadges`;
 * this hook only gathers the inputs and carries the previous runway reading so the
 * amber state keeps its hysteresis across renders.
 */
export function useOutreachNavBadges(
  startupId: string,
  options?: { enabled?: boolean }
): Record<string, NavBadgeValue> {
  const enabled = options?.enabled ?? true

  const queueQuery = useOutreachQueueQuery(startupId, { enabled })
  const statusQuery = useOutreachStatusQuery(startupId)
  const flagsQuery = useStartupFeatureFlagsQuery(startupId)
  const portfolioStatusQuery = useQuery({
    ...v2GetPortfolioOutreachStatusOptions({ path: { id: startupId } }),
    enabled,
  })
  const portfolioQueueQuery = useQuery({
    ...v2GetPortfolioQueueOptions({ path: { id: startupId } }),
    enabled,
  })
  const replies = useOutreachReplies(startupId, { enabled })
  // Home already resolves runway and unread replies for the founder wizard.
  // Reading them here is what keeps the sidebar and Home from quoting two
  // different numbers for the same thing.
  const homeStateQuery = useHomeStateQuery(startupId, { enabled })
  const home = homeStateQuery.data?.status === 'ok' ? homeStateQuery.data.data : null

  const items = queueQuery.data ?? []
  const status = statusQuery.data ?? null
  const portfolioData =
    portfolioStatusQuery.data?.status === 'ok' ? portfolioStatusQuery.data.data : null

  const portfolioFlagOn = flagsQuery.data?.portfolioOutreach ?? false
  const portfolioOn = portfolioFlagOn && (portfolioData?.enabled ?? false)

  const wasShowingRunway = useRef(false)

  const portfolioQueue =
    portfolioQueueQuery.data?.status === 'ok' ? portfolioQueueQuery.data.data : null
  // Research-phase placeholders fill the board before the campaign has drafted
  // anything. They are not a campaign, so no badge may be derived from them —
  // otherwise a founder who has not launched yet is told their runway ran out.
  const realRow = (row: { preview: boolean }) => !row.preview
  const portfolioQueued = (portfolioQueue?.queued ?? []).filter(realRow)
  const portfolioRows = portfolioQueue
    ? [
        ...portfolioQueued,
        ...portfolioQueue.active,
        ...portfolioQueue.warmIntro,
        ...portfolioQueue.finished,
      ]
    : []

  const { badges, showingRunway } = resolveOutreachNavBadges({
    approvedUnsent: items.filter((i) => i.status === 'approved').length,
    sentTimestamps: items.map((i) => i.progress?.connectionRequestSentAt),
    awaitingDecision: items.filter((i) => i.status === 'awaiting').length,
    warmIntro: {
      approvedUnsent: portfolioQueued.filter(
        (q) => q.approvalState === 'approved' && q.pushedAt == null
      ).length,
      sentTimestamps: portfolioRows.map((q) => q.connectionRequestSentAt),
      awaitingDecision: portfolioQueued.filter((q) => q.approvalState === 'awaiting').length,
      // Derived from the queue, not the status row: real queue rows only exist
      // once the campaign is built, and the portfolio status query is not
      // reliably resolved by the time the sidebar renders.
      launched: portfolioRows.length > 0,
      paused: portfolioFlagOn && portfolioData?.campaignId != null && !portfolioOn,
    },
    launched: status?.founderApprovedAt != null || status?.status === 'READY',
    investorStalled:
      status?.status === 'PAUSED' ||
      status?.status === 'PAUSED_DUE_TO_BUDGET' ||
      status?.status === 'ERROR',
    // The founder's own switch, which is not a problem to flag.
    investorPaused: status?.directInvestorOutreachEnabled === false,
    // Home already carries the top-up ask; the dots would only compete with it.
    outOfFunds: status?.status === 'PAUSED_DUE_TO_BUDGET',
    replyCount: replies.total,
    home: home
      ? {
          runwayDays: home.runwayDays,
          investorRunwayDays: home.investorTrack.runwayDays,
          warmIntroRunwayDays: home.warmIntroTrack.runwayDays,
          drainedTracks: home.drainedTracks ?? [],
          unseenReplyCount: home.replyCount,
          inSetup: SETUP_STATES.has(home.key),
        }
      : undefined,
    wasShowingRunway: wasShowingRunway.current,
  })

  wasShowingRunway.current = showingRunway

  return badges
}
